import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment, ValidationError } from '@netfoundry-ui/shared/model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { likeContains } from './frontdoor-v3-execution.service';
import { ShareHealthV3 } from './frontdoor-v3-share-health';
import { checkFrontdoorBody } from './frontdoor-v3-validation';
import { LoggerService } from './logger.service';
import { HTTP_CLIENT } from './token.service';

/** A TCP share as the v3 API returns it: the share's own fields, plus the derived `access`. */
export interface TcpShareV3 {
    [key: string]: any;

    id: string;
    frontdoorId?: string;
    /** Agents that serve the share's traffic, balanced between them. */
    activeAgentIds?: string[];
    /** Agents that serve the share's traffic only when no active agent is bound. */
    passiveAgentIds?: string[];
    name?: string;
    description?: string;
    /**
     * Where every hosting agent forwards this share's traffic, as two flat fields. The address is an
     * IPv4 or IPv6 literal, a CIDR block, or a hostname of any TLD, because the host sits inside a
     * customer network.
     */
    targetAddress?: string;
    targetPort?: number;
    /** Port on the shared mTLS ingress. Allocated by the service and unique across every account. */
    ingressPort?: number;
    /** The address a client connects to, built by the service from its ingress domain and the port. */
    access?: string;
    /** Id of the Ziti service backing this share. Absent until the service has been projected. */
    zitiServiceId?: string;
    /**
     * How far the share has been built: `NEW`, `PENDING_DEPLOY`, `DEPLOYED`, `ERROR`, `INGRESS_DELETING`,
     * `DELETING` or `DELETED`. Read it through {@link tcpShareStatus} rather than directly.
     */
    status?: string;
    /**
     * The mTLS ingress rules, in two alternative modes. `clientCertificateId` is the pinned
     * certificate, a UUID of an uploaded certificate; the four below it are the CA mode. The
     * service refuses a share that carries both.
     */
    clientCertificateId?: string;
    useIntermediateCertificate?: boolean;
    allowedSubjects?: string[];
    allowedCommonNamePrefixes?: string[];
    requiredOrganizationalUnit?: string;
    /** The server certificate the ingress presents, or null for the platform certificate. */
    serverCertificateId?: string | null;
    createdAt?: string;
    /** Where the share stands, from the port check each host runs. Absent on a create response. */
    health?: ShareHealthV3 | null;
}

/**
 * A request to create a TCP share. `ingressPort` pins the share to a known listener; leave it out
 * and the service allocates the lowest free port in its range.
 *
 * Everything from `clientCertificateId` down describes the mTLS ingress. The service stores and
 * returns those fields but nothing acts on them yet, because the ingress is provisioned elsewhere.
 */
export interface CreateTcpShareV3 {
    frontdoorId: string;
    activeAgentIds: string[];
    passiveAgentIds?: string[];
    name: string;
    targetAddress: string;
    targetPort: number;
    description?: string;
    ingressPort?: number;
    clientCertificateId?: string;
    useIntermediateCertificate?: boolean;
    allowedSubjects?: string[];
    allowedCommonNamePrefixes?: string[];
    requiredOrganizationalUnit?: string;
    /** Absent takes the account's default server certificate, null the platform certificate. */
    serverCertificateId?: string | null;
}

/** The longest name the service accepts, from ZitiUtil.MAX_SHARE_NAME_LENGTH. */
export const MAX_TCP_SHARE_NAME_LENGTH = 168;

/**
 * The ingress port range the service allocates from, from its own defaults. Server configuration,
 * so this is the hint a form shows and never the arbiter — the service answers about the range it
 * is actually running with.
 */
export const DEFAULT_TCP_INGRESS_PORT_MIN = 2100;
export const DEFAULT_TCP_INGRESS_PORT_MAX = 2199;

/**
 * A request to change a TCP share. Only the fields the body carries are applied: a sent list
 * replaces the stored one, and `null` clears `passiveAgentIds`, `description` and the single-valued
 * certificate rules. A mode switch must clear the mode it leaves, because the service checks the
 * pinned-against-CA rule on the merged share.
 */
export interface UpdateTcpShareV3 {
    name?: string;
    activeAgentIds?: string[];
    passiveAgentIds?: string[] | null;
    targetAddress?: string;
    targetPort?: number;
    ingressPort?: number;
    description?: string | null;
    clientCertificateId?: string | null;
    useIntermediateCertificate?: boolean | null;
    allowedSubjects?: string[];
    allowedCommonNamePrefixes?: string[];
    requiredOrganizationalUnit?: string | null;
    /** Absent keeps the stored certificate, null moves the share to the platform certificate. */
    serverCertificateId?: string | null;
}

/** One page of shares, plus the link that gets the next one. */
export interface TcpSharePageV3 {
    shares: TcpShareV3[];
    /** The service's own link to the next page, or '' at the end. Opaque — nothing here parses it. */
    nextHref: string;
    /** Total across all pages when the service reports one, otherwise null. */
    total: number | null;
}

export const DEFAULT_TCP_SHARE_PAGE_SIZE = 50;

/**
 * What a share listing can be narrowed by at the service. Only what `FindTcpShare` binds, so a
 * field here is one the API answers on rather than one this end drops rows for.
 */
export interface TcpShareListFilters {
    /**
     * Matched as a contains, case-insensitively — one value is read as a `LIKE`, so this is a
     * search box's value and not an exact name.
     */
    name?: string;
    /** The agents whose shares to list. Several are read as "any of these". */
    agentIds?: string[];
    /** The statuses to list, in the spelling the wire carries them in. Several are read as "any of these". */
    statuses?: string[];
    /** The health states to list, in the wire spelling. The service matches each share's stored health. */
    healths?: string[];
}

/** Adds the filters a listing was asked for. An absent or empty one is left off entirely. */
export function withShareFilters(params: HttpParams, filters: TcpShareListFilters = {}): HttpParams {
    let withFilters = params;
    const name = (filters.name ?? '').trim();
    if (name.length > 0) {
        withFilters = withFilters.set('name', likeContains(name));
    }
    // Repeated rather than joined: each parameter binds a set, and one comma-joined value would
    // be read as a single value nothing matches.
    withFilters = appendAll(withFilters, 'agentId', filters.agentIds);
    withFilters = appendAll(withFilters, 'status', filters.statuses);
    withFilters = appendAll(withFilters, 'health', filters.healths);
    return withFilters;
}

/**
 * Enough shares to count what every agent of one frontdoor hosts in a single read. A read that
 * still fell short says so through the page's `total`, so a caller can report partial counts.
 */
export const TCP_SHARE_COUNT_PAGE_SIZE = 500;

/**
 * How far a share has been built. Seven states come from the service; `unknown` is this end of the
 * wire, for a share that carries no status or one this does not know. Only `deployed` is
 * reachable — a create answers in `new` and the ingress lands afterwards.
 */
export type TcpShareStatus =
    | 'new'
    | 'pendingDeploy'
    | 'deployed'
    | 'error'
    | 'ingressDeleting'
    | 'deleting'
    | 'deleted'
    | 'unknown';

/** How a status reads on screen: the badge tone that carries it, and the words in it. */
export interface TcpShareStatusBadge {
    tone: 'ok' | 'warn' | 'err' | 'muted';
    label: string;
}

const TCP_SHARE_STATUS_BADGES: Record<TcpShareStatus, TcpShareStatusBadge> = {
    new: { tone: 'warn', label: 'New' },
    pendingDeploy: { tone: 'warn', label: 'Deploying' },
    deployed: { tone: 'ok', label: 'Deployed' },
    error: { tone: 'err', label: 'Error' },
    ingressDeleting: { tone: 'warn', label: 'Deleting Ingress' },
    deleting: { tone: 'warn', label: 'Deleting' },
    deleted: { tone: 'muted', label: 'Deleted' },
    unknown: { tone: 'muted', label: 'Unknown' },
};

/** The service's own names for those states, in the spelling the wire carries them in. */
const TCP_SHARE_WIRE_STATUSES = new Map<string, TcpShareStatus>([
    ['NEW', 'new'],
    ['PENDING_DEPLOY', 'pendingDeploy'],
    ['DEPLOYED', 'deployed'],
    ['ERROR', 'error'],
    ['INGRESS_DELETING', 'ingressDeleting'],
    ['DELETING', 'deleting'],
    ['DELETED', 'deleted'],
]);

/** The statuses a filter offers, in build order. `deleted` is left out because the listing never shows one. */
export const TCP_SHARE_STATUS_OPTIONS: TcpShareStatus[] = [
    'new',
    'pendingDeploy',
    'deployed',
    'error',
    'ingressDeleting',
    'deleting',
];

/**
 * Read the status off a share. Anything unsent or unrecognised is `unknown`, so a state added at
 * the far end reads as "not known here" rather than as one of the seven.
 */
export function tcpShareStatus(share: TcpShareV3 | undefined): TcpShareStatus {
    return TCP_SHARE_WIRE_STATUSES.get((share?.status ?? '').toUpperCase()) ?? 'unknown';
}

/** How to draw `status`. One place, because the listing and the drawer must not disagree. */
export function tcpShareStatusBadge(status: TcpShareStatus): TcpShareStatusBadge {
    return TCP_SHARE_STATUS_BADGES[status] ?? TCP_SHARE_STATUS_BADGES.unknown;
}

/** The wire spelling of a status, for a filter the service applies. `unknown` has none, so it is ''. */
export function tcpShareWireStatus(status: TcpShareStatus): string {
    for (const [wire, known] of TCP_SHARE_WIRE_STATUSES) {
        if (known === status) {
            return wire;
        }
    }
    return '';
}

/**
 * Reads TCP shares from the v3 frontdoor API. One frontdoor at a time: every path is scoped by
 * the frontdoor id, so the caller has to have chosen one before this is worth calling.
 */
@Injectable({ providedIn: 'root' })
export class FrontdoorV3ShareService {
    private readonly apiUrl: string;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.apiUrl = this.environment.frontdoorURL;
    }

    /**
     * One page of `frontdoorId`'s TCP shares, newest first unless `sort` says otherwise. The service pages by offset,
     * so `page` counts from 0 and the page's `total` counts every match. `filters` narrows the listing at the service.
     */
    public findTcpShares(
        frontdoorId: string,
        sort = 'createdAt,DESC',
        size = DEFAULT_TCP_SHARE_PAGE_SIZE,
        filters: TcpShareListFilters = {},
        page = 0
    ): Observable<TcpSharePageV3> {
        const params = withShareFilters(
            new HttpParams().set('sort', sort).set('size', String(size)).set('page', String(page)),
            filters
        );
        return this.listShares(frontdoorId, params);
    }

    /**
     * The first page of the shares `agentId` hosts, in either role, sorted by name. The service
     * applies the filter, so this is a page of that agent's shares and not a filtered first page.
     */
    public findSharesForAgent(
        frontdoorId: string,
        agentId: string,
        sort = 'name,ASC',
        size = DEFAULT_TCP_SHARE_PAGE_SIZE
    ): Observable<TcpSharePageV3> {
        const params = new HttpParams().set('agentId', agentId).set('sort', sort).set('size', String(size));
        return this.listShares(frontdoorId, params);
    }

    /** The first page of the shares that use `serverCertificateId`, sorted by name. The service applies the filter. */
    public findSharesForServerCertificate(
        frontdoorId: string,
        serverCertificateId: string,
        sort = 'name,ASC',
        size = DEFAULT_TCP_SHARE_PAGE_SIZE
    ): Observable<TcpSharePageV3> {
        const params = new HttpParams()
            .set('serverCertificateId', serverCertificateId)
            .set('sort', sort)
            .set('size', String(size));
        return this.listShares(frontdoorId, params);
    }

    /**
     * Adds a TCP share to `frontdoorId`. The body repeats the id because the service rejects a
     * body that names a different account, and the caller should not be the one to get that wrong.
     */
    public createTcpShare(frontdoorId: string, share: CreateTcpShareV3): Observable<TcpShareV3> {
        const body = { ...share, frontdoorId };
        return this.http
            .post(`${this.apiUrl}/v3/${frontdoorId}/tcp-shares`, body, { headers: this.headers() })
            .pipe(map((response: any) => response as TcpShareV3));
    }

    /**
     * Whether {@link createTcpShare} would be accepted, without creating anything. Sends the same
     * verb, path and body as the write, so the check is about the request the caller would make.
     *
     * An empty answer means the body is well formed, not that the create will succeed: an ingress
     * port is only held once the share is written. Authorized identically, so a caller without
     * create permission still gets a 403.
     */
    public validateCreateTcpShare(frontdoorId: string, share: CreateTcpShareV3): Observable<ValidationError[]> {
        const body = { ...share, frontdoorId };
        return checkFrontdoorBody(this.http, `${this.apiUrl}/v3/${frontdoorId}/tcp-shares`, body);
    }

    /**
     * Whether an update would be accepted, without changing anything. It checks with `PUT`, which
     * checks the share as it would end up, so the caller must send a complete body: `name`,
     * `activeAgentIds`, `targetAddress`, `targetPort` and `ingressPort`.
     */
    public validateUpdateTcpShare(
        frontdoorId: string,
        shareId: string,
        update: UpdateTcpShareV3
    ): Observable<ValidationError[]> {
        return checkFrontdoorBody(this.http, `${this.apiUrl}/v3/${frontdoorId}/tcp-shares/${shareId}`, update, 'PUT');
    }

    /**
     * One TCP share of `frontdoorId`. A share addressed under another account answers 404, so a
     * stale link is a miss rather than a read across accounts.
     */
    public getTcpShare(frontdoorId: string, shareId: string): Observable<TcpShareV3> {
        return this.http
            .get(`${this.apiUrl}/v3/${frontdoorId}/tcp-shares/${shareId}`, { headers: this.headers() })
            .pipe(map((response: any) => response as TcpShareV3));
    }

    /**
     * Applies `update` to one TCP share. `PATCH`, so only the fields the body carries are applied:
     * a rename renames all four Ziti objects, a new target rewrites the `host.v1` configs, and new
     * agent lists rewrite the bind policy and the passive overrides.
     */
    public updateTcpShare(frontdoorId: string, shareId: string, update: UpdateTcpShareV3): Observable<TcpShareV3> {
        return this.http
            .patch(`${this.apiUrl}/v3/${frontdoorId}/tcp-shares/${shareId}`, update, { headers: this.headers() })
            .pipe(map((response: any) => response as TcpShareV3));
    }

    /**
     * Deletes the share and the Ziti objects behind it, so it stops answering. Its ingress port
     * goes back into the pool with it.
     */
    public deleteTcpShare(frontdoorId: string, shareId: string): Observable<any> {
        return this.http.delete(`${this.apiUrl}/v3/${frontdoorId}/tcp-shares/${shareId}`, {
            headers: this.headers(),
        });
    }

    /**
     * The next page, by following the link the service returned. Taken verbatim, because the
     * cursor inside it only resumes under the order that made it.
     */
    public followNext(nextHref: string): Observable<TcpSharePageV3> {
        return this.http
            .get(nextHref, { headers: this.headers() })
            .pipe(map((response: any) => this.readPage(response)));
    }

    private listShares(frontdoorId: string, params: HttpParams): Observable<TcpSharePageV3> {
        return this.http
            .get(`${this.apiUrl}/v3/${frontdoorId}/tcp-shares`, { headers: this.headers(), params })
            .pipe(map((response: any) => this.readPage(response)));
    }

    private headers(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/hal+json');
    }

    /** Accepts the HAL collection, a bare array, or a Spring page, so the shape can settle later. */
    private readPage(response: any): TcpSharePageV3 {
        if (Array.isArray(response)) {
            return { shares: response, nextHref: '', total: response.length };
        }
        const shares = this.readList(response);
        const total = Number.isFinite(response?.page?.totalElements) ? response.page.totalElements : null;
        return { shares, nextHref: response?._links?.next?.href ?? '', total };
    }

    private readList(response: any): TcpShareV3[] {
        const embedded = response?._embedded;
        if (embedded) {
            const collection = embedded.tcpShareList ?? embedded.tcpShares ?? Object.values(embedded)[0];
            if (Array.isArray(collection)) {
                return collection;
            }
        }
        if (Array.isArray(response?.content)) {
            return response.content;
        }
        this.logger.error('findTcpShares: unrecognised response shape', response);
        return [];
    }
}

/** Repeats `name` once per value, dropping the empty ones. */
function appendAll(params: HttpParams, name: string, values: string[] | undefined): HttpParams {
    let withValues = params;
    for (const value of values ?? []) {
        if (value) {
            withValues = withValues.append(name, value);
        }
    }
    return withValues;
}
